/**
 * Soft fleet OSM road routing (browser Soft only).
 * Profiles: walk | bike | drive | air.
 *
 * Path contract Soft≠Live:
 *  · Ground → real OSRM network only (multi-host failover · no straight invent)
 *  · Air → air corridor
 *  · Destinations = Soft edge devices (p2p) unless nearby recon
 *
 * Note: host CSP must allow router.project-osrm.org + routing.openstreetmap.de
 * (see api/middleware.py connect-src). Without that, browser blocks route fetch.
 */
import { haversineM } from "./soft-fleet-geo.js";

/** Nearby recon free pad (metres from unit · away from devices). Soft only. */
export const RECON_MAX_M = 48;

/**
 * Dispatch drop snaps to a Soft edge device only inside this radius.
 * Unlimited nearest-device was snapping across the whole site (feels wrong / imprecise).
 */
export const DEVICE_SNAP_MAX_M = 72;

/** Ground already at endpoint — skip OSRM chatter. */
const AT_DEST_M = 12;

const cache = new Map();
const inflight = new Map();

/**
 * Public OSRM bases Soft research only.
 * project-osrm first; FOSSGIS routed-* profiles as failover.
 */
function osrmBasesFor(profile) {
  const p = profile === "bike" ? "bike" : profile === "drive" ? "car" : "foot";
  return [
    {
      base: "https://router.project-osrm.org/route/v1",
      prof: profile === "bike" ? "cycling" : profile === "drive" ? "driving" : "walking",
      label: "osrm_demo",
    },
    {
      base: `https://routing.openstreetmap.de/routed-${p}/route/v1`,
      prof: profile === "bike" ? "cycling" : profile === "drive" ? "driving" : "walking",
      label: "fossgis",
    },
  ];
}

/** densify polyline for smooth onboard animation (≈ stepM between pts). */
export function densifyLine(coords, stepM = 14) {
  if (!coords || coords.length < 2) return coords || [];
  const out = [coords[0].slice()];
  for (let i = 1; i < coords.length; i++) {
    const a = out[out.length - 1];
    const b = coords[i];
    const d = haversineM(a, b);
    if (d <= stepM) {
      out.push(b.slice());
      continue;
    }
    const n = Math.max(1, Math.ceil(d / stepM));
    for (let k = 1; k <= n; k++) {
      const t = k / n;
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
  }
  return out;
}

/** Soft air corridor — not road. */
export function airCorridor(from, to) {
  if (!from || !to) return [from || to].filter(Boolean);
  const mid = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const lift = 0.12;
  const bend = [mid[0] - dy * lift, mid[1] + dx * lift];
  return densifyLine([from, bend, to], 22);
}

function cacheKey(engine, profile, from, to) {
  return [
    engine,
    profile,
    from[0].toFixed(5),
    from[1].toFixed(5),
    to[0].toFixed(5),
    to[1].toFixed(5),
  ].join("|");
}

/**
 * Legal p2p endpoints: Soft edge devices only (kind=device).
 * Soft≠Live · never other tenants.
 */
export function deviceEndpoints(assets, excludeId) {
  const ex = excludeId ? String(excludeId) : "";
  return (assets || []).filter(
    (a) =>
      a &&
      a.kind === "device" &&
      Array.isArray(a.ll) &&
      a.ll.length >= 2 &&
      Number.isFinite(Number(a.ll[0])) &&
      Number.isFinite(Number(a.ll[1])) &&
      (!ex || String(a.id) !== ex),
  );
}

export function nearestEndpointDevice(assets, ll, excludeId) {
  if (!ll || ll.length < 2) return null;
  let best = null;
  let bestD = Infinity;
  for (const d of deviceEndpoints(assets, excludeId)) {
    const dist = haversineM(ll, d.ll);
    if (dist < bestD) {
      bestD = dist;
      best = d;
    }
  }
  return best ? { device: best, distM: bestD } : null;
}

/**
 * Default: snap drop to Soft edge device (p2p) within DEVICE_SNAP_MAX_M.
 * · preferDeviceId / preferLl — screen-picked pin wins (pixel-accurate drop)
 * · Recon: free pad only when drop is near the unit and not near a device
 * Soft≠Live · never unlimited cross-site nearest snaps.
 *
 * @param {object} [opts]
 * @param {string} [opts.preferDeviceId]
 * @param {number[]} [opts.preferLl]
 * @param {number} [opts.snapMaxM]
 */
export function resolveP2PDestination(assets, clickLl, movingUnit, opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const snapMax =
    Number.isFinite(Number(o.snapMaxM)) && Number(o.snapMaxM) > 0
      ? Number(o.snapMaxM)
      : DEVICE_SNAP_MAX_M;
  const click = [Number(clickLl && clickLl[0]), Number(clickLl && clickLl[1])];
  if (!Number.isFinite(click[0]) || !Number.isFinite(click[1])) {
    return { mode: "none", ll: null, deviceId: null, label: "", distM: Infinity };
  }

  /* Pixel/screen pick: operator aimed at this Soft pin — do not re-nearest away. */
  const preferId = o.preferDeviceId != null ? String(o.preferDeviceId) : "";
  if (preferId) {
    const hit = deviceEndpoints(assets, movingUnit && movingUnit.id).find(
      (d) => String(d.id) === preferId,
    );
    if (hit) {
      return {
        mode: "device",
        ll: hit.ll.slice(),
        deviceId: String(hit.id),
        label: String(hit.label || hit.id),
        distM: haversineM(click, hit.ll),
      };
    }
  }
  if (Array.isArray(o.preferLl) && o.preferLl.length >= 2) {
    const pl = [Number(o.preferLl[0]), Number(o.preferLl[1])];
    if (Number.isFinite(pl[0]) && Number.isFinite(pl[1])) {
      const nearPrefer = nearestEndpointDevice(assets, pl, movingUnit && movingUnit.id);
      if (nearPrefer && nearPrefer.distM <= Math.min(snapMax, 24)) {
        return {
          mode: "device",
          ll: nearPrefer.device.ll.slice(),
          deviceId: String(nearPrefer.device.id),
          label: String(nearPrefer.device.label || nearPrefer.device.id),
          distM: nearPrefer.distM,
        };
      }
    }
  }

  const near = nearestEndpointDevice(assets, click, movingUnit && movingUnit.id);
  const dDev = near ? near.distM : Infinity;

  if (movingUnit && Array.isArray(movingUnit.ll) && movingUnit.ll.length >= 2) {
    const dUnit = haversineM(movingUnit.ll, click);
    if (dUnit <= RECON_MAX_M && dDev > RECON_MAX_M) {
      return {
        mode: "recon",
        ll: click.slice(),
        deviceId: null,
        label: "recon pad",
        distM: dUnit,
      };
    }
  }

  /* Tight meter snap only — aim near the pin, not "nearest on whole map". */
  if (near && near.distM <= snapMax) {
    return {
      mode: "device",
      ll: near.device.ll.slice(),
      deviceId: String(near.device.id),
      label: String(near.device.label || near.device.id),
      distM: near.distM,
    };
  }

  return { mode: "none", ll: null, deviceId: null, label: "", distM: dDev };
}

/** Auto plan: Soft device for incident by skill affinity. */
export function pickIncidentAnchorDevice(assets, tpl, origin) {
  const devices = deviceEndpoints(assets);
  if (!devices.length) return null;
  const hint = tpl && (tpl.anchorDeviceId || tpl.deviceId);
  if (hint) {
    const hit = devices.find((d) => String(d.id) === String(hint));
    if (hit) return hit;
  }
  const needs = new Set((tpl && tpl.needs) || []);
  const c = origin && origin.length >= 2 ? origin : devices[0].ll;
  let best = null;
  let bestScore = -1e9;
  for (const d of devices) {
    let score = 0;
    for (const s of d.skills || []) {
      if (needs.has(s)) score += 4;
    }
    if (d.fixed) score += 0.5;
    score -= haversineM(d.ll, c) / 400;
    if (score > bestScore) {
      bestScore = score;
      best = d;
    }
  }
  return best;
}

function pathLen(coords) {
  let m = 0;
  for (let i = 1; i < coords.length; i++) m += haversineM(coords[i - 1], coords[i]);
  return m;
}

async function fetchOneOsrm(base, prof, from, to, engineLabel) {
  const key = cacheKey(engineLabel, prof, from, to);
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);

  const url =
    `${base}/${prof}/` +
    `${from[0]},${from[1]};${to[0]},${to[1]}` +
    `?overview=full&geometries=geojson&steps=false`;

  const p = (async () => {
    const res = await fetch(url, { method: "GET", mode: "cors", credentials: "omit" });
    if (!res.ok) throw new Error("osrm_http_" + res.status);
    const j = await res.json();
    if (!j || j.code !== "Ok" || !j.routes || !j.routes[0]) {
      throw new Error("osrm_" + ((j && j.code) || "empty"));
    }
    const route = j.routes[0];
    const coords = densifyLine(route.geometry.coordinates || [], 14);
    if (coords.length < 2) throw new Error("osrm_short");
    const out = {
      coords,
      distanceM: Number(route.distance) || pathLen(coords),
      durationS: Number(route.duration) || pathLen(coords) / 1.4,
      profile: prof === "cycling" ? "bike" : prof === "driving" ? "drive" : "walk",
      fromName: (j.waypoints && j.waypoints[0] && j.waypoints[0].name) || "path",
      toName: (j.waypoints && j.waypoints[1] && j.waypoints[1].name) || "path",
      engine: "osrm_osm",
      engineHost: engineLabel,
    };
    cache.set(key, out);
    return out;
  })();

  inflight.set(key, p);
  try {
    return await p;
  } finally {
    inflight.delete(key);
  }
}

/**
 * OSM road leg · multi-host. Soft research only · never invents straight cut.
 */
export async function fetchOsrmLeg(profile, from, to) {
  const hosts = osrmBasesFor(profile);
  let lastErr = null;
  for (const h of hosts) {
    try {
      return await fetchOneOsrm(h.base, h.prof, from, to, h.label);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("osrm_all_hosts_fail");
}

/**
 * Resolve route for skill profile.
 * air → corridor; walk/bike/drive → OSRM only.
 */
export async function resolveSkillRoute(asset, dest, meta) {
  const profile = (asset && asset.routeProfile) || "walk";
  const destLabel = (meta && meta.label) || "";
  const destMode = (meta && meta.mode) || "device";
  const from = asset && asset.ll;
  if (!from || !dest || dest.length < 2) {
    throw new Error("route_missing_ll");
  }

  if (profile === "air") {
    const coords = airCorridor(from, dest);
    const distanceM = pathLen(coords);
    const speed = asset.speed > 0 ? asset.speed : 12;
    return {
      coords,
      distanceM,
      durationS: distanceM / speed,
      profile: "air",
      fromName: "airlift",
      toName: destLabel || (destMode === "recon" ? "recon pad" : "overwatch"),
      engine: "air_corridor",
      destDeviceId: (meta && meta.deviceId) || null,
      destMode,
    };
  }
  if (profile === "static") {
    return {
      coords: [from.slice(), from.slice()],
      distanceM: 0,
      durationS: 0,
      profile: "static",
      fromName: "on_station",
      toName: "on_station",
      engine: "static",
      destDeviceId: (meta && meta.deviceId) || null,
      destMode,
    };
  }

  /* Already on pad — honest zero route, no OSRM. */
  if (haversineM(from, dest) <= AT_DEST_M) {
    return {
      coords: densifyLine([from.slice(), dest.slice()], 8),
      distanceM: haversineM(from, dest),
      durationS: 1,
      profile,
      fromName: "at",
      toName: destLabel || "endpoint",
      engine: "osrm_at_pad",
      destDeviceId: (meta && meta.deviceId) || null,
      destMode,
    };
  }

  const leg = await fetchOsrmLeg(profile, from, dest);
  if (asset.speed > 0 && leg.distanceM > 0) {
    const physics = leg.distanceM / asset.speed;
    if (profile === "walk") leg.durationS = Math.max(leg.durationS * 0.92, physics * 0.85);
    else leg.durationS = Math.max(physics, leg.durationS * 0.55);
  }
  if (destLabel) leg.toName = destLabel;
  leg.destDeviceId = (meta && meta.deviceId) || null;
  leg.destMode = destMode;
  return leg;
}
